import db from "../db/database";
import { tiendaDelNumero } from "../utils/numeroDeCajaDeBC";
import { getTPVByNoOrId } from "./tpvService";
import { getCajaByNoOrId } from "./cajaService";
import { resolverAlmacenDeLaCaja } from "./tiendaDeLaCaja";
import { laMasReciente, cajasConVariasAbiertas } from "./aperturaMasReciente";

// Clave usada para almacenar la información de caja activa en localStorage como respaldo
const ACTIVE_CAJA_KEY = "exceliaone_caja_activa";

// Clave usada para almacenar la caja usada en el último Traer Histórico exitoso
const HISTORICO_CAJA_KEY = "exceliaone_caja_historico";

/**
 * Aviso de que la caja activa cambió — se abrió, se cerró, o la apertura que
 * estaba abierta pasó a otra caja.
 *
 * Quien muestre "en qué caja estoy" lo escucha y se recarga en el momento. El
 * indicador de la barra superior consultaba cada 30 segundos y nada más, así
 * que después de editar la apertura seguía anunciando la caja anterior hasta
 * que le tocara el turno al reloj — la pantalla decía una cosa y la base otra.
 *
 * Va por evento del navegador y no por contexto de React porque quien cambia la
 * caja activa es un servicio, no un componente. Es el mismo mecanismo que ya
 * usan "syncStatus" y "syncFailures".
 */
export const EVENTO_CAJA_ACTIVA = "cajaActivaCambiada";

/** El respaldo guardado, sin el sello de tiempo, para poder compararlo. */
const sinSello = (guardado) => {
  try {
    const { timestamp, ...resto } = JSON.parse(guardado);
    return resto;
  } catch (error) {
    return null;
  }
};

const avisarCambioDeCajaActiva = () => {
  try {
    window.dispatchEvent(new CustomEvent(EVENTO_CAJA_ACTIVA));
  } catch (error) {
    // Un aviso que no se puede emitir no puede tumbar la operación que lo
    // provocó: la caja ya cambió, esto sólo refresca lo que se ve.
    console.warn("No se pudo avisar el cambio de caja activa:", error);
  }
};

// Aperturas traídas por pull de histórico (pullAperturasFromBC) pueden haber
// quedado guardadas con TPV: null de antes de que ese pull empezara a
// derivarlo desde el "No" -- se deriva acá también, en lectura, para que
// esos registros ya guardados se autocorrijan sin tener que volver a
// traerlos (el pull dedupea por No, así que un fix solo en el pull no los
// alcanza). Formato de No: "TPV;Caja;Fecha".
const withResolvedTPV = (apertura) => {
  if (!apertura || apertura.TPV) return apertura;
  const tpvFromNo = tiendaDelNumero(apertura.No);
  return tpvFromNo ? { ...apertura, TPV: tpvFromNo } : apertura;
};

/**
 * Obtiene la información de la apertura de caja actualmente activa
 * @returns {Promise<Object|null>} Información de la apertura activa o null si no hay ninguna
 */
export const getAperturaActiva = async () => {
  try {
    // Intentar obtener apertura activa desde localStorage (respaldo)
    const localData = localStorage.getItem(ACTIVE_CAJA_KEY);
    let aperturaActivaId = localData ? JSON.parse(localData).id : null;

    // Buscar aperturas con estado "Abierto"
    const aperturasAbiertas = await db.aperturaDeCaja
      .filter((apertura) => apertura.Estado === "Abierto" && !apertura.deleted)
      .toArray();

    // Si hay varias abiertas, manda la que el cajero ya tenía activa; si esa se
    // cerró (o nunca hubo), la más reciente. Antes se tomaba `[0]` con el
    // comentario «la primera (la más reciente)», y no lo era: `filter().toArray()`
    // devuelve en orden de clave primaria, o sea la MÁS ANTIGUA. Medido en el
    // ambiente de pruebas de Ecuador el 03/09/2026, cinco cajas tenían más de una
    // apertura abierta a la vez --hasta tres-- así que la elección no era teórica:
    // las ventas del turno caían en un turno que alguien creía cerrado.
    if (aperturasAbiertas.length > 0) {
      const laQueYaEstaba = aperturasAbiertas.find((a) => a.id === aperturaActivaId);
      const aperturaActiva = withResolvedTPV(laQueYaEstaba ?? laMasReciente(aperturasAbiertas));

      const varias = cajasConVariasAbiertas(aperturasAbiertas);
      if (varias.length > 0) {
        console.warn(
          "Hay cajas con más de una apertura abierta a la vez:",
          varias.map(({ caja, cuantas }) => `${caja} (${cuantas})`).join(", ")
        );
      }

      await setAperturaActiva(aperturaActiva);
      return aperturaActiva;
    }

    // Si no hay aperturas abiertas pero hay ID en localStorage
    if (aperturaActivaId) {
      const apertura = await db.aperturaDeCaja.get(aperturaActivaId);
      // Si la apertura existe pero no está abierta, limpiar el estado
      if (!apertura || apertura.Estado !== "Abierto" || apertura.deleted) {
        await clearAperturaActiva();
        return null;
      }
      return withResolvedTPV(apertura);
    }

    return null;
  } catch (error) {
    console.error("Error al obtener apertura activa:", error);
    return null;
  }
};

/**
 * Establece una apertura como la caja actualmente activa
 * @param {Object} apertura - Objeto con la información de la apertura
 * @returns {Promise<boolean>} True si se estableció correctamente
 */
export const setAperturaActiva = async (apertura) => {
  try {
    if (!apertura || !apertura.id) {
      throw new Error("Apertura inválida");
    }

    const resumen = {
      id: apertura.id,
      No: apertura.No,
      Cajero: apertura.Cajero,
      Caja: apertura.Caja,
      TPV: apertura.TPV,
      Turno: apertura.Turno,
    };

    // El aviso sólo sale si algo cambió de verdad.
    //
    // getAperturaActiva() llama acá en CADA lectura para refrescar el respaldo,
    // y el indicador de la barra lee al recibir el aviso: avisar siempre sería
    // un lazo infinito (leer -> avisar -> leer). El sello de tiempo se compara
    // aparte, porque cambia solo y no es un cambio de caja.
    const anterior = localStorage.getItem(ACTIVE_CAJA_KEY);
    const cambio = JSON.stringify(resumen) !== JSON.stringify(anterior ? sinSello(anterior) : null);

    // Guardar en localStorage como respaldo
    localStorage.setItem(
      ACTIVE_CAJA_KEY,
      JSON.stringify({ ...resumen, timestamp: new Date().toISOString() })
    );

    if (cambio) avisarCambioDeCajaActiva();

    return true;
  } catch (error) {
    console.error("Error al establecer apertura activa:", error);
    return false;
  }
};

/**
 * Limpia la información de caja activa
 * @returns {Promise<boolean>} True si se limpió correctamente
 */
export const clearAperturaActiva = async () => {
  try {
    const habia = localStorage.getItem(ACTIVE_CAJA_KEY) !== null;
    localStorage.removeItem(ACTIVE_CAJA_KEY);
    if (habia) avisarCambioDeCajaActiva();
    return true;
  } catch (error) {
    console.error("Error al limpiar apertura activa:", error);
    return false;
  }
};

/**
 * Verifica si hay una caja abierta actualmente
 * @returns {Promise<boolean>} True si hay una caja abierta
 */
export const hayCajaAbierta = async () => {
  const aperturaActiva = await getAperturaActiva();
  return !!aperturaActiva;
};

/**
 * Obtiene la información básica de la caja activa para mostrar en UI
 * @returns {Promise<Object>} Objeto con información de la caja activa
 */
export const getInfoCajaActiva = async () => {
  const apertura = await getAperturaActiva();
  if (!apertura) return null;

  return {
    id: apertura.id,
    No: apertura.No,
    Cajero: apertura.Cajero,
    Caja: apertura.Caja,
    TPV: apertura.TPV,
    Turno: apertura.Turno,
    FechaDeApertura: apertura.FechaDeApertura,
    HoraDeApertura: apertura.HoraDeApertura,
  };
};

/**
 * Obtiene la caja (y el período de fechas) usados en el último Traer
 * Histórico exitoso, mientras esos datos locales sigan vigentes (se limpia
 * al cambiar de caja o al cerrar caja). El período se usa, entre otras cosas,
 * para que el pull de histórico de Apertura de Caja (botones de sincronizar
 * y sincronización general) traiga el mismo rango que ya trajo TH, en vez de
 * inventar uno propio.
 * @returns {{ Caja: string, FechaInicio: string|null, FechaFin: string|null, timestamp: string } | null}
 */
export const getCajaHistorico = () => {
  try {
    const data = localStorage.getItem(HISTORICO_CAJA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error al obtener caja de histórico:", error);
    return null;
  }
};

/**
 * Guarda la caja (y opcionalmente el período de fechas) usados en un Traer
 * Histórico exitoso.
 * @param {string} caja
 * @param {{ FechaInicio?: string, FechaFin?: string }} [periodo]
 */
export const setCajaHistorico = (caja, periodo) => {
  try {
    if (!caja) {
      localStorage.removeItem(HISTORICO_CAJA_KEY);
      return true;
    }
    localStorage.setItem(
      HISTORICO_CAJA_KEY,
      JSON.stringify({
        Caja: caja,
        FechaInicio: periodo?.FechaInicio || null,
        FechaFin: periodo?.FechaFin || null,
        timestamp: new Date().toISOString(),
      })
    );
    return true;
  } catch (error) {
    console.error("Error al establecer caja de histórico:", error);
    return false;
  }
};

/**
 * Limpia el vínculo de caja de histórico (al cambiar de caja o cerrar caja).
 */
export const clearCajaHistorico = () => {
  try {
    localStorage.removeItem(HISTORICO_CAJA_KEY);
    return true;
  } catch (error) {
    console.error("Error al limpiar caja de histórico:", error);
    return false;
  }
};

/**
 * Obtiene la apertura de la última caja cerrada localmente (sin importar si
 * hubo Traer Histórico de por medio). A diferencia de getCajaHistorico, esto
 * no se limpia al cerrar caja -- al contrario, se calcula on-demand a partir
 * de las aperturas ya cerradas, así que sirve para casos donde el usuario
 * solo abrió caja, facturó y cerró caja, sin usar Traer Histórico nunca.
 * @returns {Promise<Object|null>} La apertura cerrada más reciente, o null
 */
export const getUltimaCajaCerrada = async () => {
  try {
    const cerradas = await db.aperturaDeCaja
      .filter((apertura) => apertura.Estado === "Cerrado" && !apertura.deleted)
      .toArray();

    if (!cerradas.length) return null;

    cerradas.sort((a, b) => new Date(b.lastModified || 0) - new Date(a.lastModified || 0));

    return cerradas[0];
  } catch (error) {
    console.error("Error al obtener la última caja cerrada:", error);
    return null;
  }
};

/**
 * Resuelve el locationCode (tienda) del TPV asociado a la caja actualmente
 * activa. Devuelve null si no hay caja abierta o el TPV no tiene locationCode
 * configurado (ej. países que todavía no usan stock por tienda).
 * @returns {Promise<string|null>}
 */
export const almacenDeLaCajaActiva = async () => {
  try {
    const apertura = await getAperturaActiva();
    return await resolverAlmacenDeLaCaja(
      apertura,
      async (no) => {
        // Devuelve un objeto, y lanza si no encuentra la caja. Aquí no
        // encontrarla es una respuesta --el maestro está sin sincronizar--, no
        // una avería: se traduce a null y el resolutor dice qué motivo es.
        try {
          return await getCajaByNoOrId(no);
        } catch {
          return null;
        }
      },
      (no) => getTPVByNoOrId(no)
    );
  } catch (error) {
    console.error("Error al resolver el almacén de la caja activa:", error);
    return { locationCode: null, motivo: null, tienda: null, caja: null };
  }
};

export const getActiveLocationCode = async () => (await almacenDeLaCajaActiva()).locationCode;
